import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Stack,
  Skeleton,
} from "@mui/material";
import axios from "axios";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useNavigate } from "react-router-dom";
import config from "../../Config";

export default function RelatedProducts({ subCategory }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      if (!subCategory) {
        setProducts([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const response = await axios.get(
          `${config.apiBaseUrl}/${config.relatedProducts}`,
          {
            headers: {
              "sub-id": subCategory,
            },
          },
        );

        const productList = Array.isArray(response.data?.Products)
          ? response.data.Products
          : [];

        const mappedProducts = productList
          .filter((item) => item?.product?.productId)
          .map((item) => ({
            id: item.product.productId,
            name: item.product.title || "Untitled Piece",
            price: item.product.rentAmount ?? 0,
            image: item.productImage?.[0] || "",
            city: item.address?.city || "Location unavailable",
            availableDate:
              item.slots?.availableDate || "Availability unavailable",
            size: item.product.size || "",
          }));

        setProducts(mappedProducts);
      } catch (error) {
        console.error("Error fetching related products:", error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [subCategory]);

  const handleProductClick = (productId) => {
    navigate(`/productdetail/${productId}`);
  };

  if (loading) {
    return (
      <Box
        sx={{
          mt: {
            xs: 6,
            md: 9,
          },
        }}
      >
        <Box sx={{ mb: 4 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: 1.5 }}
          >
            <Box
              sx={{
                width: 32,
                height: 1,
                background: "#977743",
              }}
            />

            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: "#977743",
              }}
            >
              You may also like
            </Typography>
          </Stack>

          <Skeleton
            variant="text"
            width={220}
            height={48}
            sx={{
              transform: "none",
              background: "#e9e4dc",
            }}
          />
        </Box>

        <Grid container spacing={{ xs: 2, sm: 2.5, md: 2 }}>
          {[1, 2, 3, 4, 5].map((item) => (
            <Grid item key={item} xs={12} sm={6} md={2.4}>
              <Box>
                <Skeleton
                  variant="rectangular"
                  sx={{
                    width: "100%",
                    aspectRatio: "0.78",
                    background: "#e9e4dc",
                  }}
                />

                <Box sx={{ pt: 1.5 }}>
                  <Skeleton
                    variant="text"
                    width="75%"
                    sx={{ background: "#e9e4dc" }}
                  />

                  <Skeleton
                    variant="text"
                    width="45%"
                    sx={{ background: "#e9e4dc" }}
                  />
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (products.length === 0) {
    return (
      <Box
        sx={{
          mt: {
            xs: 6,
            md: 9,
          },
          py: {
            xs: 5,
            md: 7,
          },
          px: 3,
          textAlign: "center",
          borderTop: "1px solid #ddd7ce",
          borderBottom: "1px solid #ddd7ce",
        }}
      >
        <Typography
          sx={{
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: "#977743",
            mb: 1.5,
          }}
        >
          Collection Update
        </Typography>

        <Typography
          sx={{
            fontFamily: "Georgia, serif",
            fontSize: {
              xs: 27,
              md: 34,
            },
            fontWeight: 400,
            color: "#171717",
          }}
        >
          No related pieces available
        </Typography>

        <Typography
          sx={{
            maxWidth: 450,
            mx: "auto",
            mt: 1.5,
            fontSize: 12,
            lineHeight: 1.8,
            color: "#77716a",
          }}
        >
          Explore our wider collection to discover more pieces curated for your
          next occasion.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: {
          xs: 6,
          md: 9,
        },
      }}
    >
      <Box
        sx={{
          mb: {
            xs: 3,
            md: 4,
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ mb: 1.3 }}
        >
          <Box
            sx={{
              width: 34,
              height: 1,
              background: "#977743",
            }}
          />

          <Typography
            sx={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: "#977743",
            }}
          >
            You may also like
          </Typography>
        </Stack>

        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "flex-start",
            sm: "flex-end",
          }}
          spacing={1}
        >
          <Typography
            component="h2"
            sx={{
              fontFamily: "Georgia, serif",
              fontSize: {
                xs: 30,
                sm: 36,
                md: 42,
              },
              fontWeight: 400,
              lineHeight: 1.1,
              letterSpacing: "-0.035em",
              color: "#171717",
            }}
          >
            Related Pieces
          </Typography>

          <Typography
            sx={{
              fontSize: 10,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "#77716a",
            }}
          >
            {products.length} {products.length === 1 ? "piece" : "pieces"}
          </Typography>
        </Stack>
      </Box>

      <Grid container spacing={{ xs: 2, sm: 2.5, md: 2 }}>
        {products.map((product) => (
          <Grid item key={product.id} xs={12} sm={6} md={2.4}>
            <Card
              elevation={0}
              onClick={() => handleProductClick(product.id)}
              sx={{
                height: "100%",
                borderRadius: 0,
                border: "1px solid #ddd7ce",
                background: "#fff",
                cursor: "pointer",
                overflow: "hidden",
                transition:
                  "transform 300ms ease, box-shadow 300ms ease, border-color 300ms ease",
                "&:hover": {
                  transform: "translateY(-5px)",
                  borderColor: "#b9aa92",
                  boxShadow: "0 18px 45px rgba(23,23,23,0.1)",
                  "& .related-product-image": {
                    transform: "scale(1.045)",
                  },
                  "& .related-product-arrow": {
                    opacity: 1,
                    transform: "translateX(0)",
                  },
                },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: "100%",
                  aspectRatio: "0.78",
                  overflow: "hidden",
                  background: "#f1eee8",
                }}
              >
                {product.image ? (
                  <CardMedia
                    component="img"
                    image={product.image}
                    alt={product.name}
                    className="related-product-image"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transition:
                        "transform 700ms cubic-bezier(0.2, 0.7, 0.2, 1)",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#eeebe5",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Georgia, serif",
                        fontSize: 15,
                        color: "#aaa39a",
                      }}
                    >
                      Hindustan Wardrobe
                    </Typography>
                  </Box>
                )}

                <Box
                  sx={{
                    position: "absolute",
                    top: 12,
                    left: 12,
                    px: 1.2,
                    py: 0.7,
                    background: "rgba(255,255,255,0.94)",
                    backdropFilter: "blur(5px)",
                    border: "1px solid rgba(221,215,206,0.8)",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 7,
                      fontWeight: 700,
                      letterSpacing: "0.15em",
                      textTransform: "uppercase",
                      color: "#171717",
                    }}
                  >
                    Rental
                  </Typography>
                </Box>

                <Box
                  className="related-product-arrow"
                  sx={{
                    position: "absolute",
                    right: 12,
                    bottom: 12,
                    width: 34,
                    height: 34,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#171717",
                    color: "#fff",
                    opacity: 0,
                    transform: "translateX(6px)",
                    transition: "opacity 250ms ease, transform 250ms ease",
                  }}
                >
                  <ArrowForwardRoundedIcon sx={{ fontSize: 17 }} />
                </Box>
              </Box>

              <CardContent
                sx={{
                  p: {
                    xs: 1.5,
                    md: 1.7,
                  },
                  "&:last-child": {
                    pb: {
                      xs: 1.5,
                      md: 1.7,
                    },
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Georgia, serif",
                    fontSize: {
                      xs: 16,
                      md: 15,
                    },
                    lineHeight: 1.25,
                    fontWeight: 400,
                    color: "#171717",
                    mb: 0.8,
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {product.name}
                </Typography>

                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#171717",
                    mb: 1.2,
                  }}
                >
                  ₹{product.price}{" "}
                  <Box
                    component="span"
                    sx={{
                      fontSize: 9,
                      fontWeight: 400,
                      color: "#8b857d",
                    }}
                  >
                    / day
                  </Box>
                </Typography>

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{ mb: 0.8 }}
                >
                  <LocationOnOutlinedIcon
                    sx={{
                      fontSize: 14,
                      color: "#977743",
                    }}
                  />

                  <Typography
                    sx={{
                      fontSize: 9,
                      color: "#77716a",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {product.city}
                  </Typography>
                </Stack>

                <Stack direction="row" alignItems="flex-start" spacing={0.7}>
                  <Box
                    sx={{
                      width: 5,
                      height: 5,
                      mt: "5px",
                      flexShrink: 0,
                      borderRadius: "50%",
                      background: "#977743",
                    }}
                  />

                  <Typography
                    sx={{
                      fontSize: 8,
                      lineHeight: 1.5,
                      color: "#99938b",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {product.availableDate}
                  </Typography>
                </Stack>

                {product.size && (
                  <Box
                    sx={{
                      mt: 1.2,
                      pt: 1.1,
                      borderTop: "1px solid #eee9e2",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 8,
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "#99938b",
                      }}
                    >
                      Size{" "}
                      <Box
                        component="span"
                        sx={{
                          color: "#171717",
                        }}
                      >
                        {product.size}
                      </Box>
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}
